/**
 * Feature flags for the recording UI.
 *
 * Provides a single source of truth for "can the user record X" so a bad
 * rollout can be disabled quickly without touching the deploy pipeline.
 *
 * Resolution order (first writer wins for each flag):
 *   1. `window.__FF_FLAGS__` — runtime global, useful for DevTools console
 *      overrides (e.g. `window.__FF_FLAGS__ = { allowVideoRecording: false }`
 *      then hard reload).
 *   2. localStorage `ff.featureFlags.v1` — stickier per-browser override.
 *      Set via `localStorage.setItem('ff.featureFlags.v1', JSON.stringify(...))`.
 *   3. Compiled defaults below.
 *
 * To kill-switch a feature for everyone, change the default here and redeploy
 * the static bundle (~5 min). For a faster path, add a Firestore-backed
 * config read on app load and extend `getFeatureFlags` to merge it in as a
 * fourth source — the rest of the app already treats the return of
 * `getFeatureFlags()` as the ground truth.
 */

const DEFAULTS = Object.freeze({
  // Master switch for the audio recorder. When false, the recorder UI is
  // disabled entirely (no record button surfaces in the practice block).
  allowAudioRecording: true,

  // Master switch for the video recorder. When false, the Video mode toggle
  // is disabled exactly like when the browser doesn't support video
  // recording — audio still works.
  allowVideoRecording: true,

  // When false, video recordings skip the streaming (chunked upload +
  // assembly CF + transcode CF) pipeline and fall back to a single-blob
  // upload at session finalize — the same path iOS/WebKit takes. Useful if
  // the CF pipeline is misbehaving and you want to force all video through
  // the simpler, deploy-free path.
  allowStreamingVideo: true
});

const LOCAL_STORAGE_KEY = 'ff.featureFlags.v1';

function readOverrides() {
  const overrides = {};
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') Object.assign(overrides, parsed);
      }
    }
  } catch (e) { /* noop */ }
  try {
    if (typeof window !== 'undefined' && window.__FF_FLAGS__ && typeof window.__FF_FLAGS__ === 'object') {
      Object.assign(overrides, window.__FF_FLAGS__);
    }
  } catch (e) { /* noop */ }
  return overrides;
}

export function getFeatureFlags() {
  return { ...DEFAULTS, ...readOverrides() };
}

// Helper for single-flag access with the same precedence.
export function isFeatureEnabled(flagName) {
  const flags = getFeatureFlags();
  return !!flags[flagName];
}
